/**
 * Which APPLICATION (and which release-management environment of it) the
 * page is about — sent with every request as `X-Twasta-Project` /
 * `X-Twasta-Owner` / `X-Twasta-Env`.
 *
 * An application under release management reads different files and a
 * different database depending on who asks and where: an organization's users
 * run their pinned release, builders the working copy, a QC tester the
 * staging copy of one fix. The backend decides that once per request
 * (`middleware/sdlc_env`), and these headers are how a request names the
 * application it is about — most endpoints carry the project in the path
 * under different names, so the middleware cannot reliably read it there.
 *
 * It is a CONSISTENCY hint, not an access control: every endpoint still
 * authenticates and authorizes the caller. For an application that is not
 * versioned the middleware ignores the headers entirely.
 *
 * Set by the Run view (project + owner, and `env` from `?env=stage:<n>`) and
 * the builder workbench (the current project). A PROJECT USER's Run session
 * names its application with its own `X-Twasta-App-*` headers instead
 * (`runSession`), and the client sends these only when that session is not in
 * force — the two must never both speak for one request.
 */

export interface AppEnv {
  project: string
  /** Platform id of the application's owner, when known. */
  owner?: string
  /** `stage:<n>` (QC on a fix's staging copy) or `prod`. */
  env?: string
  /** Which surface bound it — so one surface's teardown never clears a
   *  binding another surface has just made (route changes interleave them). */
  source?: 'run' | 'workbench'
}

let current: AppEnv | null = null

const ENV_KEY = (project: string) => `twasta_app_env::${project}`

/** Name the application this page is about (null clears it). */
export function setAppEnv(next: AppEnv | null): void {
  current = next && next.project ? { ...next } : null
}

/** Clear the binding, but only if `source` is the surface that made it. */
export function clearAppEnv(source: AppEnv['source']): void {
  if (current && current.source === source) current = null
}

/** Change part of the current binding (e.g. the owner once it is resolved). */
export function updateAppEnv(patch: Partial<AppEnv>): void {
  if (!current) return
  current = { ...current, ...patch }
}

export function getAppEnv(): AppEnv | null {
  return current
}

/**
 * The requested environment for `project` in THIS tab. A Run URL carries it
 * once (`?env=stage:42`); the view's own navigation then drops the query, so
 * it is remembered for the tab's lifetime — closing the tab ends staging, a
 * reload does not.
 */
export function rememberRequestedEnv(project: string, env: string | null): void {
  try {
    if (env) sessionStorage.setItem(ENV_KEY(project), env)
    else sessionStorage.removeItem(ENV_KEY(project))
  } catch {
    // Private mode: the env holds for this page load only.
  }
}

export function requestedEnv(project: string): string | null {
  try { return sessionStorage.getItem(ENV_KEY(project)) } catch { return null }
}

/** A requested env is only ever one of these shapes; anything else is dropped. */
export function sanitizeEnv(raw: string | null | undefined): string | null {
  const v = (raw || '').trim().toLowerCase()
  if (/^stage:#?\d{1,9}$/.test(v)) return v.replace('#', '')
  if (v === 'prod' || v === 'production') return 'prod'
  return null
}

/** The headers for the binding in force; empty when nothing is bound. */
export function appEnvHeaders(): Record<string, string> {
  // A header value must be printable ASCII; a name outside that would make
  // the browser refuse the WHOLE request, so such a project simply goes
  // unnamed (the middleware then leaves it unbound, as for any other app).
  if (!current?.project || !/^[\x20-\x7e]+$/.test(current.project)) return {}
  const out: Record<string, string> = { 'X-Twasta-Project': current.project }
  if (current.owner) out['X-Twasta-Owner'] = current.owner
  if (current.env) out['X-Twasta-Env'] = current.env
  return out
}
